// src/services/LoggingService.js - Updated for authenticated users only
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { db } from '../firebase/config';
import {
  collection,
  addDoc,
  query,
  where,
  limit,
  Timestamp,
  getDocs,
} from 'firebase/firestore';

const LOGS_COLLECTION = 'logs';

// Off unless explicitly enabled at build time. The logs collection is never
// read by anything (no admin screen, no backend code), and every entry is a
// billed write on the named `wafflestop` database, which has no free quota.
// It was also effectively off for customers already: its old connection test
// queried the whole logs collection, which the rules refused for non-admins -
// while for admins it succeeded and read EVERY log document on each app load.
const CLIENT_LOGGING_ENABLED = process.env.REACT_APP_ENABLE_CLIENT_LOGGING === 'true';
const LOG_RETENTION_DAYS = 7;
const MAX_RETRY_ATTEMPTS = 3;
const RETRY_DELAY = 2000; // 2 seconds

// Log levels
export const LOG_LEVEL = {
  ERROR: 'error',
  WARNING: 'warning',
  INFO: 'info',
  DEBUG: 'debug',
};

// Log categories
export const LOG_CATEGORY = {
  ERROR: 'error',
  USER_INTERACTION: 'user_interaction',
  NAVIGATION: 'navigation',
  PERFORMANCE: 'performance',
  NETWORK: 'network',
  AUTH: 'auth',
};

class LoggingService {
  constructor() {
    this.enabled = CLIENT_LOGGING_ENABLED;
    this.offlineMode = false;
    this.queuedLogs = [];
    this.isProcessingQueue = false;
    this.connectionMonitorActive = false;
    this.authReady = false;
    this.currentUser = null;
    this.authUnsubscribe = null;

    // Disabled: no auth listener, no connection test, no Firestore traffic at
    // all. log() already returns early when !this.enabled.
    if (!this.enabled) return;

    // Monitor online/offline status
    window.addEventListener('online', this.handleOnline.bind(this));
    window.addEventListener('offline', this.handleOffline.bind(this));

    // Set up auth state monitoring
    this.initAuthStateListener();
  }

  // Initialize authentication state listener
  initAuthStateListener() {
    const auth = getAuth();
    
    this.authUnsubscribe = onAuthStateChanged(auth, (user) => {
      const wasAuthenticated = !!this.currentUser;
      const isNowAuthenticated = !!user;
      
      this.currentUser = user;
      this.authReady = true;
      
      console.log('Auth state changed:', {
        was_authenticated: wasAuthenticated,
        is_now_authenticated: isNowAuthenticated,
        user_id: user?.uid || null
      });

      // If user just became authenticated, process queued logs
      if (!wasAuthenticated && isNowAuthenticated) {
        console.log('User authenticated, processing queued logs');
        this.processQueuedLogs();
      }

      // If user became unauthenticated, set offline mode
      if (wasAuthenticated && !isNowAuthenticated) {
        console.log('User unauthenticated, entering offline mode');
        this.offlineMode = true;
      }

      // Test connection when auth state is ready
      if (this.authReady) {
        this.testConnection();
      }
    });
  }

  // Test connection based on auth state
  async testConnection() {
    if (!this.authReady) {
      console.log('Auth not ready yet, skipping connection test');
      return;
    }

    try {
      if (this.currentUser) {
        // User is authenticated, test with logs collection. Scoped to the
        // user's own entries (the only query the rules accept) and capped at
        // one document - it is a reachability check, not a read of the logs.
        console.log('Testing connection with authenticated user:', this.currentUser.uid);
        const testQuery = query(
          collection(db, LOGS_COLLECTION),
          where('userId', '==', this.currentUser.uid),
          limit(1)
        );
        await getDocs(testQuery);
        console.log('Firestore connection successful for authenticated user');
        this.offlineMode = false;
      } else {
        // User not authenticated, test with public collection
        console.log('Testing connection without authentication');
        const testQuery = query(
          collection(db, 'system_status'),
          where('type', '==', 'connection_test'),
          limit(1)
        );
        await getDocs(testQuery);
        console.log('Firestore connection successful (unauthenticated)');
        // Keep offline mode true since we need auth for logging
        this.offlineMode = true;
      }
    } catch (error) {
      console.warn('Firestore connection test failed:', error);
      
      if (error.code === 'permission-denied') {
        if (!this.currentUser) {
          console.log('Permission denied - user not authenticated. Logs will be queued.');
          this.offlineMode = true; // Queue logs until authenticated
        } else {
          console.error('Permission denied for authenticated user. Logging disabled.');
          this.enabled = false;
        }
      } else {
        this.offlineMode = true;
      }
    }
  }

  // Monitor connection state with a simpler approach
  startConnectionMonitor() {
    if (this.connectionMonitorActive) return;

    // Simple periodic connection test
    this.connectionInterval = setInterval(async () => {
      if (this.offlineMode && navigator.onLine && this.currentUser) {
        await this.testConnection();
        if (!this.offlineMode) {
          this.processQueuedLogs();
        }
      }
    }, 30000); // Check every 30 seconds

    this.connectionMonitorActive = true;
  }

  // Handle device going online
  handleOnline() {
    console.log('Device is online. Testing connection...');
    this.testConnection().then(() => {
      if (!this.offlineMode && this.currentUser) {
        this.processQueuedLogs();
      }
    });
  }

  // Handle device going offline
  handleOffline() {
    console.log('Device is offline. Queuing logs locally.');
    this.offlineMode = true;
  }

  // Process any logs that were queued while offline or unauthenticated
  async processQueuedLogs() {
    if (
      this.isProcessingQueue ||
      this.queuedLogs.length === 0 ||
      this.offlineMode ||
      !this.enabled ||
      !this.currentUser
    ) {
      return;
    }

    this.isProcessingQueue = true;
    console.log(`Processing ${this.queuedLogs.length} queued logs for user ${this.currentUser.uid}`);

    try {
      // Process logs individually to avoid batch size issues
      const logsToProcess = [...this.queuedLogs];
      this.queuedLogs = [];
      let successCount = 0;

      for (const logEntry of logsToProcess) {
        try {
          // Update userId to current authenticated user if it was null
          if (!logEntry.userId && this.currentUser) {
            logEntry.userId = this.currentUser.uid;
          }
          
          await addDoc(collection(db, LOGS_COLLECTION), logEntry);
          successCount++;
        } catch (error) {
          console.error('Failed to write queued log:', error);
          // If it fails, put it back in the queue unless it's a permission error
          if (error.code !== 'permission-denied') {
            this.queuedLogs.push(logEntry);
          } else {
            console.warn('Permission denied for queued log, discarding');
          }
        }
      }

      console.log(`Successfully processed ${successCount} queued logs`);
    } catch (error) {
      console.error('Error processing queued logs:', error);
    } finally {
      this.isProcessingQueue = false;
    }
  }

  /**
   * Log an event to Firestore with authentication check
   */
  async log(
    level,
    category,
    message,
    data = {},
    userId = null,
    retryAttempt = 0
  ) {
    if (!this.enabled) {
      return;
    }

    // Wait for auth to be ready
    if (!this.authReady) {
      console.log('Auth not ready yet, queuing log');
      const logEntry = this.createLogEntry(level, category, message, data, userId);
      this.queuedLogs.push(logEntry);
      return;
    }

    // If no user is authenticated, queue the log
    if (!this.currentUser && !userId) {
      console.log('No authenticated user, queuing log');
      const logEntry = this.createLogEntry(level, category, message, data, userId);
      this.queuedLogs.push(logEntry);
      return;
    }

    try {
      // Use current authenticated user if no userId provided
      const effectiveUserId = userId || this.currentUser?.uid;
      const logEntry = this.createLogEntry(level, category, message, data, effectiveUserId);

      // If we're offline or in a known bad connection state, queue for later
      if (this.offlineMode || !navigator.onLine) {
        this.queuedLogs.push(logEntry);
        if (!this.connectionMonitorActive) {
          this.startConnectionMonitor();
        }
        return;
      }

      // Try to write directly to Firestore
      await addDoc(collection(db, LOGS_COLLECTION), logEntry);
      console.log('Log written successfully:', { level, category, message, userId: effectiveUserId });
    } catch (error) {
      console.error('Logging error:', error);

      // Handle different error scenarios
      if (error.code === 'permission-denied') {
        if (!this.currentUser) {
          console.log('Permission denied - user not authenticated. Queuing log.');
          const logEntry = this.createLogEntry(level, category, message, data, userId);
          this.queuedLogs.push(logEntry);
          this.offlineMode = true;
          
          if (!this.connectionMonitorActive) {
            this.startConnectionMonitor();
          }
        } else {
          console.error('Permission denied for authenticated user. Disabling logging.');
          this.enabled = false;
        }
        return;
      }

      if (
        !navigator.onLine ||
        error.code === 'unavailable' ||
        error.code === 'resource-exhausted'
      ) {
        // Network error or service unavailable - queue the log
        this.offlineMode = true;
        const logEntry = this.createLogEntry(level, category, message, data, userId || this.currentUser?.uid);
        this.queuedLogs.push(logEntry);

        if (!this.connectionMonitorActive) {
          this.startConnectionMonitor();
        }
      } else if (retryAttempt < MAX_RETRY_ATTEMPTS) {
        // Retry for other transient errors with exponential backoff
        const delay = RETRY_DELAY * Math.pow(2, retryAttempt);

        setTimeout(() => {
          this.log(level, category, message, data, userId, retryAttempt + 1);
        }, delay);
      }
    }
  }

  // Helper method to create log entry
  createLogEntry(level, category, message, data, userId) {
    const timestamp = Timestamp.now();
    const expiresAt = Timestamp.fromDate(
      new Date(timestamp.toDate().getTime() + LOG_RETENTION_DAYS * 24 * 60 * 60 * 1000)
    );

    return {
      level,
      category,
      message,
      data,
      userId: userId || this.currentUser?.uid || null,
      timestamp,
      expiresAt,
      userAgent: navigator.userAgent,
      path: window.location.pathname,
    };
  }

  async logError(message, error = {}, userId = null) {
    const errorData =
      error instanceof Error
        ? {
            name: error.name,
            message: error.message,
            stack: error.stack,
          }
        : error;

    await this.log(LOG_LEVEL.ERROR, LOG_CATEGORY.ERROR, message, errorData, userId);
  }

  async logUserInteraction(action, data = {}, userId = null) {
    await this.log(
      LOG_LEVEL.INFO,
      LOG_CATEGORY.USER_INTERACTION,
      action,
      data,
      userId
    );
  }

  async logNavigation(from, to, userId = null) {
    await this.log(
      LOG_LEVEL.INFO,
      LOG_CATEGORY.NAVIGATION,
      `Navigation from ${from} to ${to}`,
      { from, to },
      userId
    );
  }

  async logAuth(action, userId = null, data = {}) {
    await this.log(LOG_LEVEL.INFO, LOG_CATEGORY.AUTH, action, data, userId);
  }

  async logPerformance(name, duration, data = {}, userId = null) {
    await this.log(
      LOG_LEVEL.INFO,
      LOG_CATEGORY.PERFORMANCE,
      name,
      { ...data, duration },
      userId
    );
  }

  async logNetwork(url, method, status, duration, data = {}, userId = null) {
    await this.log(
      LOG_LEVEL.INFO,
      LOG_CATEGORY.NETWORK,
      `${method} ${url}`,
      { ...data, method, status, duration },
      userId
    );
  }

  // Cleanup method
  destroy() {
    if (this.connectionInterval) {
      clearInterval(this.connectionInterval);
    }
    if (this.unsubscribe) {
      this.unsubscribe();
    }
    if (this.authUnsubscribe) {
      this.authUnsubscribe();
    }
    window.removeEventListener('online', this.handleOnline.bind(this));
    window.removeEventListener('offline', this.handleOffline.bind(this));
  }
}

export default new LoggingService();
